import React from 'react';
import Image from '../layout/Image';
import thumbImage from '../../images/placeholder.gif';
import { getAreaFromCoordinates } from '../../lib/AreaUtils';
import styles from './locationStyles.module.scss';

export type LocationType = RetirementVillageLocationFragment | NursingHomeLocationFragment;

interface Props {
  item: LocationType;
}

// Helper function to extract the first sentence from HTML content
const getFirstSentence = (htmlContent: string | null | undefined): string => {
  if (!htmlContent) return '';

  // Match the first <p> tag and extract its content
  const match = htmlContent.match(/<p[^>]*>(.*?)<\/p>/i);
  if (match && match[1]) {
    // Strip any remaining HTML tags from the paragraph content
    let text = match[1].replace(/<[^>]*>/g, '').trim();

    // Find the first full stop and cut the text there
    const firstFullStop = text.indexOf('.');
    if (firstFullStop !== -1) {
      text = text.substring(0, firstFullStop + 1);
    }

    return text;
  }

  return '';
};

// Helper function to get activity titles as a list
const getActivityTitles = (item: LocationType): string[] => {
  if (item.__typename === 'retirementVillageLocations_retirementVillageLocations_Entry' && item.activities) {
    return item.activities.map(activity => activity?.title || '').filter(title => title !== '');
  }
  return [];
};

const LocationCard = ({ item }: Props) => {
  const title = item.title!;
  const images = item.thumbnail?.length ? item.thumbnail : item.photos;
  const firstSentence = getFirstSentence(item.body);
  const activityTitles = getActivityTitles(item);

  // Check if rental - handle Radio Buttons field values from Craft CMS
  const isRetirementVillage = item.__typename == 'retirementVillageLocations_retirementVillageLocations_Entry';
  const rentalOnlyValue = isRetirementVillage && 'rentalOnly' in item ? item.rentalOnly : null;
  const isRental = rentalOnlyValue === 'true' || rentalOnlyValue === '1';

  const link = `https://www.google.com/maps/search/?api=1&query=${item.address?.address}`;
  const area = getAreaFromCoordinates(item.address?.lat, item.address?.lng);

  return (
    <div
      className={styles.cardItem}
      data-rental={isRental ? 'true' : 'false'}
      data-area={area || 'unknown'}
    >

        <a className={styles.thumbImage} href={`/${item.uri}`} aria-label={title} title={'Click to view ' + title}>
            {images ? <Image {...images[0]} alt={title} /> : <img loading="lazy" src={thumbImage} alt={title} />}
            {isRental && <div className={styles.rentalTag}>Rental Only</div>}
        </a>

        <div className={styles.cardContent}>
            <h3>
                <a className={styles.linkEffects} href={`/${item.uri}`} title={'Click to view ' + title}>
                    {title}
                </a>
            </h3>

            {link &&
                <a href={link} className={`${styles.address} ${styles.linkEffects}`} target="_blank" rel="noopener noreferrer" title={'Click for directions to ' + title}>
                    {item.address?.parts?.city}
                </a>
            }

            {firstSentence &&
                <p>{firstSentence}</p>
            }

            {activityTitles.length > 0 && (
              <ul className={styles.activityList}>
                {activityTitles.map((activityTitle, index) => (
                  <li key={index}>{activityTitle}</li>
                ))}
              </ul>
            )}

            <div className={styles.urlContainer}>
                <a className={styles.linkEffects} href={`/${item.uri}`} title={'Click to view ' + title}>
                    View {title} <span>&#8250;</span>
                </a>
            </div>
        </div>
    </div>
  );
};

export default LocationCard;
